import axios from 'axios';

// Configuración base de axios
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
});

// Interceptor para agregar token de autenticación (futuro)
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Error de servidor con respuesta
      console.error('Error de API:', error.response.data);
      return Promise.reject(error.response.data);
    } else if (error.request) {
      // Error de red
      console.error('Error de red:', error.request);
      return Promise.reject({ message: 'Error de conexión con el servidor' });
    } else {
      // Otro tipo de error
      console.error('Error:', error.message);
      return Promise.reject({ message: error.message });
    }
  }
);

// =====================
// CLIENTES API
// =====================

export const clientesAPI = {
  getAll: () => api.get('/clientes'),
  getById: (id) => api.get(`/clientes/${id}`),
  create: (clienteData) => api.post('/clientes', clienteData),
  update: (id, clienteData) => api.put(`/clientes/${id}`, clienteData),
  delete: (id) => api.delete(`/clientes/${id}`),
};

// =====================
// RECIBOS API
// =====================

export const recibosAPI = {
  getAll: (params = {}) => api.get('/recibos', { params }),
  getById: (id) => api.get(`/recibos/${id}`),
  getByCliente: (clienteId, params = {}) => 
    api.get(`/recibos/cliente/${clienteId}`, { params }),
  create: (reciboData) => api.post('/recibos', reciboData),
  update: (id, reciboData) => api.put(`/recibos/${id}`, reciboData),
  delete: (id) => api.delete(`/recibos/${id}`),
  cancelar: (id, data) => api.put(`/recibos/${id}/cancelar`, data),
};

// =====================
// RECIBOS RECURRENTES API
// =====================

export const recibosRecurrentesAPI = {
  getAll: (params = {}) => api.get('/recibos-recurrentes', { params }),
  getById: (id) => api.get(`/recibos-recurrentes/${id}`),
  getByCliente: (clienteId) => api.get(`/recibos-recurrentes/cliente/${clienteId}`),
  create: (reciboData) => api.post('/recibos-recurrentes', reciboData),
  update: (id, reciboData) => api.put(`/recibos-recurrentes/${id}`, reciboData),
  delete: (id) => api.delete(`/recibos-recurrentes/${id}`),
};

// =====================
// PAGOS API
// =====================

export const pagosAPI = {
  getAll: (params = {}) => api.get('/pagos', { params }),
  getById: (id) => api.get(`/pagos/${id}`),
  getByRecibo: (reciboId) => api.get(`/pagos/recibo/${reciboId}`),
  create: (pagoData) => api.post('/pagos', pagoData),
  update: (id, pagoData) => api.put(`/pagos/${id}`, pagoData),
  delete: (id) => api.delete(`/pagos/${id}`),
};

// =====================
// ALERTAS API
// =====================

export const alertasAPI = {
  getAll: (params = {}) => api.get('/alertas', { params }),
  getById: (id) => api.get(`/alertas/${id}`),
  getEstadisticas: () => api.get('/alertas/estadisticas'),
  create: (alertaData) => api.post('/alertas', alertaData),
  update: (id, alertaData) => api.put(`/alertas/${id}`, alertaData),
  marcarLeidas: (ids) => api.put('/alertas/marcar-leidas', { ids }),
  delete: (id) => api.delete(`/alertas/${id}`),
  generarAutomaticas: () => api.post('/alertas/generar'),
};

// =====================
// REPORTES API
// =====================

export const reportesAPI = {
  getDashboardCompleto: () => api.get('/reportes/dashboard-completo'),
  getIngresosPorMes: (anio) => 
    api.get('/reportes/ingresos-por-mes', { params: { anio } }),
  getClientesTop: (params = {}) => 
    api.get('/reportes/clientes-top', { params }),
  getMetodosPago: (params = {}) => 
    api.get('/reportes/metodos-pago', { params }),
  getEstadoCuentas: (clienteId) => 
    api.get('/reportes/estado-cuentas', { params: { clienteId } }),
  getRecibosVencidos: (diasVencido) => 
    api.get('/reportes/recibos-vencidos', { params: { diasVencido } }),
  getResumenGeneral: (params = {}) => 
    api.get('/reportes/resumen-general', { params }),
};

// =====================
// DASHBOARD API
// =====================

export const dashboardAPI = {
  getStats: () => api.get('/dashboard'),
};

// =====================
// UTILIDADES
// =====================

// Función helper para formatear fechas para la API
export const formatDateForAPI = (date) => {
  if (!date) return null;
  if (typeof date === 'string') return date;
  
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Función helper para parsear respuestas de la API
export const parseAPIResponse = (response) => {
  if (response && response.data) {
    return {
      success: response.data.success,
      data: response.data.data,
      message: response.data.message,
    };
  }
  return {
    success: false,
    data: null,
    message: 'Respuesta inválida del servidor',
  };
};

// =====================
// CONFIGURACIÓN API
// =====================

export const configuracionAPI = {
  // Obtener configuración
  getConfiguracion: () => api.get('/configuracion'),
  
  // Actualizar configuración
  updateConfiguracion: (data) => api.put('/configuracion', data),
};

// =====================
// AUTENTICACIÓN API
// =====================

export const authAPI = {
  // Login
  login: (credentials) => api.post('/auth/login', credentials).then(res => res.data),
  
  // Logout
  logout: () => api.post('/auth/logout').then(res => res.data),
  
  // Verificar sesión
  verifySession: () => api.get('/auth/verify').then(res => res.data),
};

export default api;
